import { type FC, type SyntheticEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Badge, Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { ModalCloseButton } from "@packsize/components";

import type { MachineFaultDisplay } from "@/schemas";

interface ErrorsAndWarningsModalTriggerProps {
	readonly faults: MachineFaultDisplay[];
	readonly onOpen: () => void;
}

const ErrorsAndWarningsModalTrigger: FC<ErrorsAndWarningsModalTriggerProps> = ({ faults, onOpen }) => {
	const intl = useIntl();
	const theme = useTheme();

	if (faults.length <= 0) {
		return null;
	}

	const hasErrors = faults.some((fault) => fault.type === "Error");
	const triggerBackground = hasErrors ? theme.palette.error.main : theme.palette.warning.main;

	return (
		<Badge badgeContent={faults.length} invisible={faults.length <= 1} color={hasErrors ? "warning" : "info"}>
			<IconButton
				onClick={onOpen}
				aria-label={intl.formatMessage({
					id: "Show errors and warnings",
					defaultMessage: "Show errors and warnings",
				})}
				sx={{
					borderRadius: 1,
					color: "common.white",
					backgroundColor: triggerBackground,
					"&:hover": {
						filter: "brightness(0.9)",
						backgroundColor: triggerBackground,
					},
				}}
				data-pendoid="errors-and-warnings-modal__button__trigger"
			>
				{hasErrors === true && <ErrorOutlineRoundedIcon />}
				{hasErrors === false && <WarningAmberRoundedIcon />}
			</IconButton>
		</Badge>
	);
};

export interface ErrorsAndWarningsModalTemplateProps {
	readonly faults: MachineFaultDisplay[];
	readonly open: boolean;
	readonly currentIndex: number;
	readonly onOpen: () => void;
	readonly onClose: () => void;
	readonly onPrevious: () => void;
	readonly onNext: () => void;
}

export const ErrorsAndWarningsModalTemplate: FC<ErrorsAndWarningsModalTemplateProps> = ({
	faults,
	open,
	currentIndex,
	onOpen,
	onClose,
	onPrevious,
	onNext,
}) => {
	const intl = useIntl();
	const theme = useTheme();
	const currentDisplayItem = faults[currentIndex] ?? faults[0];

	const isError = currentDisplayItem?.type === "Error";
	const severityColor = isError ? theme.palette.error.main : theme.palette.warning.main;
	const SeverityIcon = isError ? ErrorOutlineRoundedIcon : WarningAmberRoundedIcon;

	const hasResolution = Boolean(currentDisplayItem?.resolution);
	const hasImage = Boolean(currentDisplayItem?.image);

	const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
		const fallbackImage = currentDisplayItem?.fallbackImage;

		if (fallbackImage === undefined) {
			return;
		}

		event.currentTarget.onerror = null;
		event.currentTarget.src = fallbackImage;
	};

	return (
		<>
			<ErrorsAndWarningsModalTrigger faults={faults} onOpen={onOpen} />
			<Dialog
				open={open}
				fullWidth
				maxWidth="lg"
				onClose={onClose}
				slotProps={{
					paper: {
						sx: {
							border: 16,
							borderColor: severityColor,
						},
					},
				}}
			>
				<DialogTitle
					component="div"
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
						px: 0,
						pt: 0,
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							padding: 2,
							color: "common.white",
							backgroundColor: severityColor,
						}}
					>
						<SeverityIcon sx={{ fontSize: 48 }} />
					</Box>

					<Box>
						<Typography variant="h2">{currentDisplayItem?.title}</Typography>
						{hasResolution === true && <Typography>{currentDisplayItem.resolution}</Typography>}
					</Box>

					<ModalCloseButton
						size="large"
						onClick={onClose}
						aria-label={intl.formatMessage({ id: "Close", defaultMessage: "Close" })}
						sx={{
							position: "absolute",
							top: 1,
							right: 1,
						}}
						data-pendoid="errors-and-warnings-modal__button__close"
					/>
				</DialogTitle>
				<DialogContent
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						height: 400,
						p: 0,
						mx: 2,
						mb: 2,
					}}
				>
					{hasImage === true && (
						<Box
							component="img"
							src={currentDisplayItem.image}
							alt=""
							onError={handleImageError}
							sx={{
								display: "block",
								maxWidth: "100%",
								maxHeight: "100%",
								objectFit: "contain",
							}}
						/>
					)}
				</DialogContent>

				{faults.length > 1 && (
					<DialogActions
						sx={{
							justifyContent: "space-between",
							pb: 2,
							px: 2,
						}}
					>
						<IconButton onClick={onPrevious} size="large" data-pendoid="errors-and-warnings-modal__button__previous">
							<KeyboardArrowLeftRoundedIcon fontSize="large" />
						</IconButton>

						<Typography>
							<FormattedMessage
								id="Error or warning {current} of {total}"
								defaultMessage="{current} of {total}"
								values={{ current: currentIndex + 1, total: faults.length }}
							/>
						</Typography>

						<IconButton onClick={onNext} size="large" data-pendoid="errors-and-warnings-modal__button__next">
							<KeyboardArrowRightRoundedIcon fontSize="large" />
						</IconButton>
					</DialogActions>
				)}
			</Dialog>
		</>
	);
};
